import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import PanelPostEditor from './PanelPostEditor';
import {
  claimReturn,
  hasPendingReturn,
  peekReturn,
  rememberReturn,
} from '@lib/services/storeManager/pendingCardReturn';
import { getShopifyScopeUpgradeUrl } from '@lib/services/shopify/scopeUpgradeApi';
import {
  checkShopifyReauthorize,
  SHOPIFY_REAUTHORIZE_FALLBACK_SCOPES,
} from '@lib/services/shopify/shopifyReauthorize';
import { ShopifyScopeError } from '@lib/services/shopify/scopeError';
import { useNavigate } from 'react-router-dom';
import { BuildingStorefrontIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useFailurePanelStore } from '@stores/failurePanelStore';
import { useAuthStore } from '@stores/authStore';
import {
  actOnCard,
  CardActionConflict,
  getRecoveryBadgeCount,
  getRecoveryPanel,
  type CardAction,
  type RecoveryCard,
  type RecoverySummaryLine,
} from '@lib/services/storeManager/recoveryCardApi';

/**
 * [Infra 1.5 #25] 店长面板。任务失败后的恢复动作全部收进这里（任务列表只读、只留 Delete）。
 *
 * <p>V0.9 之后的卡是「一个主动作，或 Delete task」：没有 Later / Snoozed / × / Skip /
 * Report a problem / Retry，卡上不做任何输入（URL / 改名 / 选时间）。布局只认 `shape` ——
 * <b>merged</b>（同因合并：一行一条卡住的任务，3 + N more 展开）、<b>open_post</b>（Maya 内容类，
 * 主动作原地修帖）、<b>notice</b>（只告知 + Delete）。卡左侧只有一个字母头像（M / O / A；
 * 全局卡 S / $），第二行只写「agent · 任务类型」，不写 session 名和商品路径（V0.9③④）。
 *
 * <p>数据与动作全部走后端（`recoveryCardApi`）：文案、合并、排序都是处置表在服务端定的，
 * 这里从不按分类分支。每次按钮点击带卡的 token；409 = 卡在商家眼皮下动了，用返回的当前卡
 * 静默重画，不重试点击。动作落地后重拉整个面板 —— 一张卡收起往往连带别的卡
 * （一次 Shopify 重连能同时清掉断连卡和骑着同一次断连的授权卡）。
 */

/** 字母头像。agent 字母紫底；S（Shopify）/ $（Credit）这类全局卡用深底区分「不是某个 agent 的事」。 */
function LetterAvatar({ letter, small = false }: { letter: string; small?: boolean }) {
  const agent = letter === 'M' || letter === 'O' || letter === 'A';
  const size = small ? 'h-6 w-6 text-[11px]' : 'h-9 w-9 text-sm';
  return (
    <span
      className={`flex ${size} shrink-0 items-center justify-center rounded-full font-bold ${
        agent ? 'bg-[#f0ebff] text-[#6b33ff]' : 'bg-[#322d40] text-white'
      }`}
    >
      {letter}
    </span>
  );
}

const AGENT_NAME: Record<string, string> = {
  M: 'Maya',
  O: 'Oscar',
  A: 'Anna',
  S: 'Shopify',
  $: 'Credit',
};

/**
 * navigate 动作的路由名 → 前端路径。后端只说「去哪一类地方」（integrations.shopify /
 * integrations.setup / billing.topup），路径归前端。
 */
const NAVIGATE_PATHS: Record<string, string> = {
  'integrations.shopify': '/platform/settings#integrations',
  'integrations.setup': '/platform/settings#integrations',
  // Top up 弹窗住在任务中心头部的 Credit 条上。
  'billing.topup': '/platform/task-center',
};

/**
 * [YEPAI-12 C8] 缺 Shopify 权限的「Re-authorize Shopify」。不是一个页面路径 —— 它要的是 Shopify 的
 * 授权同意页（managed install），链接由 `/integration/shopify/scope-upgrade-url` 按店铺现算。
 * 曾经复用 `integrations.shopify`（→ Settings），那里只有店铺连接，点了进不了任何授权。
 */
const SHOPIFY_REAUTHORIZE_ROUTE = 'shopify.reauthorize';

/**
 * 全产品同一个「Reconnect your Shopify store」弹窗。懒加载：面板挂在 DashboardLayout 的每一页上，
 * 而这个弹窗（连同 HeroUI 的 Modal）只有点了 Re-authorize 才用得到。
 */
const ShopifyReconnectDialog = lazy(() =>
  import('../../shopify/ShopifyReconnectDialog').then(m => ({ default: m.ShopifyReconnectDialog }))
);

function reauthorizeScopes(action: CardAction): string[] {
  const listed = (action.navigate?.params?.scopes ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  return listed.length > 0 ? listed : SHOPIFY_REAUTHORIZE_FALLBACK_SCOPES;
}

function RecoveryCardView({
  card,
  focused,
  busy,
  onAction,
}: {
  card: RecoveryCard;
  focused: boolean;
  busy: boolean;
  onAction: (card: RecoveryCard, action: CardAction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const items = card.items ?? [];
  const shownItems = expanded ? items : items.slice(0, 3);
  const moreCount = items.length - shownItems.length;

  // Delete 是灰字不是按钮（V0.9①）；其余动作里 primary 紫底，剩下的描边。
  const deleteAction = card.actions.find(a => a.id === 'delete');
  const buttons = card.actions.filter(a => a.id !== 'delete');
  const agentName = AGENT_NAME[card.avatar] ?? card.avatar;

  return (
    <div
      className={`rounded-xl border bg-white p-4 transition ${
        focused ? 'border-[#6b33ff] ring-1 ring-[#6b33ff]/30' : 'border-[#e2e8f0]'
      }`}
    >
      {/* 标题行：字母头像 + 出了什么事 + 「agent · 任务类型」 */}
      <div className="flex items-start gap-3">
        <LetterAvatar letter={card.avatar} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight text-[#322d40]">{card.headline}</p>
          {card.taskKindLabel ? (
            <p className="mt-1 text-xs font-medium text-[#747c80]">
              {agentName} · {card.taskKindLabel}
            </p>
          ) : null}
        </div>
      </div>

      {/* merged：一行一条卡住的任务，跨 agent 的行带自己的小头像；3 + N more 展开（V0.9③） */}
      {card.shape === 'merged' && items.length > 0 ? (
        <div className="ml-12 mt-3 space-y-2">
          {shownItems.map(item => (
            <div key={item.taskId} className="flex items-start gap-2">
              <LetterAvatar letter={item.avatar} small />
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="truncate text-xs font-semibold text-[#322d40]">{item.label}</p>
                {item.reason ? (
                  <p className="text-[11px] leading-4 text-[#747c80]">{item.reason}</p>
                ) : null}
              </div>
            </div>
          ))}
          {moreCount > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs font-semibold text-[#6b33ff] transition hover:underline"
            >
              {moreCount} more ▾
            </button>
          ) : null}
        </div>
      ) : null}

      {card.consequence ? (
        <p className="ml-12 mt-2 text-xs font-medium leading-4 text-[#322d40]">
          {card.consequence}
        </p>
      ) : null}

      {/* 动作区：主动作一个按钮；hint 是按钮下那行灰字（按了会发生什么） */}
      {buttons.length > 0 ? (
        <div className="ml-12 mt-3">
          <div className="flex flex-wrap items-center gap-2">
            {buttons.map(a => (
              <button
                key={a.id}
                type="button"
                disabled={busy}
                onClick={() => onAction(card, a)}
                className={
                  a.primary
                    ? 'inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg bg-[#6b33ff] px-4 text-xs font-semibold text-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition hover:bg-[#5a29e0] disabled:opacity-60'
                    : 'inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg border border-[#e2e8f0] px-4 text-xs font-semibold text-[#322d40] transition hover:bg-[#f8f8fb] disabled:opacity-60'
                }
              >
                {a.label}
              </button>
            ))}
          </div>
          {buttons.find(a => a.primary)?.hint ? (
            <p className="mt-1.5 text-[11px] leading-4 text-[#747c80]">
              {buttons.find(a => a.primary)?.hint}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* 右下灰字 Delete task / Delete N tasks —— 卡仅有的另一个去向（V0.9①），label 后端给 */}
      {deleteAction ? (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={() => onAction(card, deleteAction)}
            className="whitespace-nowrap text-xs font-medium text-[#98a2b3] transition hover:text-[#667085] hover:underline disabled:opacity-60"
          >
            {deleteAction.label}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * [infra1.5 验收 #1②] 面板该亮哪张卡。
 *
 * <p>从任务中心失败行打开时带着那条任务的 id：合并卡（`items` 一行一条任务）只在里面**真有这条
 * 任务**时才亮 —— 服务端的 cardId 指错时（实测 Maya 的失败行亮了 Oscar 的卡），宁可一张都不亮。
 * 单条卡没有 items 可对，只能信 cardId。没带任务 id（简报、店长浮标）照旧只看 cardId。
 */
export function isFocusedCard(
  card: RecoveryCard,
  focusCardId: string | null,
  focusTaskId: string | null
): boolean {
  const items = card.items ?? [];
  if (focusTaskId && items.length > 0) return items.some(item => item.taskId === focusTaskId);
  return focusCardId != null && card.id === focusCardId;
}

/**
 * 未处理卡数（FAB 红点 / Home 状态行）。后端 badge 端点是为「每页都画红点」准备的便宜查询；
 * 面板关闭时重取一次 —— 刚处理完的卡要立刻从计数里消失。
 */
export function useUnhandledFailureCount(): number {
  const userId = useAuthStore(s => s.user?.id);
  const open = useFailurePanelStore(s => s.open);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId || open) return;
    let cancelled = false;
    getRecoveryBadgeCount(userId)
      .then(n => {
        if (!cancelled) setCount(n);
      })
      .catch(() => {
        /* 红点画不出来不该打扰任何页面 */
      });
    return () => {
      cancelled = true;
    };
  }, [userId, open]);

  return count;
}

/**
 * 入口：左下店长头像 + 红点。无未处理卡不出现。
 *
 * <p><b>[2026-09-23] 只在 Task Center 渲染。</b>原来按 Infra 1.5 #25「入口只在任务中心 / Home
 * 两处」也挂在 Home 上，现改为只留任务中心。「入口在哪」由渲染位置保证 ——
 * `FailurePanelEntryMountedOnce.test.ts` 钉住了它只出现在 `TaskCenterPage` 一处。
 *
 * <p>Home 上打开同一个面板的路仍在：`AgentStatusLine` 的状态行、简报里的
 * `AnythingToWatchSegment`。面板遮罩（{@link StoreManagerPanel}）挂在 DashboardLayout，与入口无关。
 */
export function FailurePanelEntry() {
  const open = useFailurePanelStore(s => s.open);
  const openPanel = useFailurePanelStore(s => s.openPanel);
  const count = useUnhandledFailureCount();
  if (count === 0 || open) return null;
  return (
    <button
      type="button"
      onClick={() => openPanel()}
      aria-label={`Store Manager · ${count} to handle`}
      className="group fixed bottom-6 left-6 z-[70] flex items-end gap-3 text-left lg:left-[248px]"
    >
      <span className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-[#eaeff2] transition group-hover:shadow-xl">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f0ebff] text-[#6b33ff]">
          <BuildingStorefrontIcon className="h-5 w-5" />
        </span>
        <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#eb001b] px-1 text-[11px] font-bold text-white ring-2 ring-white">
          {count}
        </span>
      </span>
      {/* [infra1.5 验收 #3] 浮标旁的气泡（「Stuck on a few things? … Most I can clear myself —
          click me.」）需求里没有，整块去掉：面板上的卡都要商家自己处理，店长清不掉；拟人口吻也
          不对 —— 这是固定流程 + 按钮，不是对话机器人。数字只留头像上的红点，按卡数。 */}
    </button>
  );
}

/**
 * 面板遮罩本体（左下弹出）。在 DashboardLayout 挂一次即可（portal 到 body，逃出侧边栏的层叠上下文）。
 */
export function StoreManagerPanel() {
  const navigate = useNavigate();
  const userId = useAuthStore(s => s.user?.id);
  const open = useFailurePanelStore(s => s.open);
  const focusCardId = useFailurePanelStore(s => s.focusCardId);
  const focusTaskId = useFailurePanelStore(s => s.focusTaskId);
  const closePanel = useFailurePanelStore(s => s.closePanel);
  const openPanel = useFailurePanelStore(s => s.openPanel);
  const bumpCards = useFailurePanelStore(s => s.bumpCards);

  const [cards, setCards] = useState<RecoveryCard[]>([]);
  const [summary, setSummary] = useState<RecoverySummaryLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyCardId, setBusyCardId] = useState<string | null>(null);
  /** 正在原地编辑的那一篇（Open post）。面板留在原位，编辑器盖在它上面。 */
  const [editing, setEditing] = useState<{
    card: RecoveryCard;
    action: CardAction;
    draftId: string;
  } | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const panel = await getRecoveryPanel(userId);
      setCards(panel.cards);
      setSummary(panel.summary);
      // 面板外面也有人在画这批卡（简报的「Anything to watch」，#12）。动作还在飞的时候
      // 商家就把面板关了的话，外面那次拉到的是没处理完的旧数据，而 open 已经不会再变 ——
      // 所以这里补一声，让外面跟得上。
      bumpCards();
    } catch {
      /* 面板打开着，旧卡比空屏诚实；下一次动作/开合还会再拉 */
    }
  }, [userId, bumpCards]);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [open, userId, refresh]);

  /** 缺权限时弹的「Reconnect your Shopify store」（同一个弹窗全产品复用）。 */
  const [reauthorizeError, setReauthorizeError] = useState<ShopifyScopeError | null>(null);
  /** 一次只兑现一张便条：挂载与 focus 可能同时触发。 */
  const settlingRef = useRef(false);

  /**
   * 商家从站外授权 / 充值回来了。
   *
   * <p>「回来」是我们能拿到的唯一信号 —— 授权在别人的页面上完成，没有任何回调打给我们。所以
   * 后端把要 POST 的 id 放在 navigate 里，由这一段兑现：重开面板、把那张卡打勾、顺带把它扣着的
   * 任务重新驱起来（后端 BE-10 就挂在这个 action 上）。
   *
   * <p>取一次就消费掉。回来这件事只算一次，否则此后每一次页面加载都会再把那批任务重跑一遍。
   *
   * <p>[YEPAI-12 C8] Shopify 重新授权的便条带着 scopes：授权页开在新标签，商家可能没点同意就回来了，
   * 所以先问 Shopify 这些权限到没到，到了才兑现；没到便条留着，下次回来再问。
   *
   * @param onlyVerifiable 只处理带 scopes 的便条 —— 页面重新拿到焦点时用。不带 scopes 的
   *                       （Reconnect / Top up）是整页跳走再回来的，仍只在应用启动时兑现，
   *                       不能因为商家切了个标签就当成「已修好」。
   */
  const settleReturn = useCallback(
    async (onlyVerifiable: boolean) => {
      if (!userId || settlingRef.current || !hasPendingReturn()) return;
      const peeked = peekReturn();
      if (!peeked) {
        if (!onlyVerifiable) claimReturn(); // 清掉过期 / 坏掉的便条
        return;
      }
      const scopes = peeked.scopes ?? [];
      if (onlyVerifiable && scopes.length === 0) return;
      settlingRef.current = true;
      try {
        if (scopes.length > 0) {
          try {
            const check = await getShopifyScopeUpgradeUrl(userId, scopes);
            if (!check.ok) return;
          } catch {
            return; // 查不到就当还没好：错按一次会把仍缺权限的任务再跑一遍
          }
        }
        const pending = claimReturn();
        if (!pending) return;
        openPanel(pending.cardId);
        try {
          await actOnCard(userId, pending.cardId, pending.actionId, pending.token ?? '');
        } catch {
          /* 卡可能已经被别的方式收起了（任务自己成功、商家删掉）。这里出错不必打扰商家 ——
             他刚做完一件对的事，看到的应该是少了一张卡，不是一个报错。 */
        }
        await refresh();
      } finally {
        settlingRef.current = false;
      }
    },
    [userId, openPanel, refresh]
  );

  useEffect(() => {
    void settleReturn(false);
  }, [settleReturn]);

  useEffect(() => {
    const onBack = () => {
      if (document.visibilityState === 'visible') void settleReturn(true);
    };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      window.removeEventListener('focus', onBack);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [settleReturn]);

  /**
   * [YEPAI-12 C8] Re-authorize Shopify：问清缺哪些权限、拿到 Shopify 的授权链接，用全产品同一个
   * 「Reconnect your Shopify store」弹窗交给商家（链接是 `<a target=_blank>`，由商家亲手点，
   * 不会被弹窗拦截）。已经都授权了（比如商家在别处刚授过）就直接兑现，不再绕一圈。
   */
  const startShopifyReauthorize = async (card: RecoveryCard, action: CardAction) => {
    if (!userId) return;
    const scopes = reauthorizeScopes(action);
    const confirmActionId = action.navigate?.confirmActionId ?? null;
    setBusyCardId(card.id);
    try {
      const check = await checkShopifyReauthorize(userId, scopes);
      if (check.granted) {
        if (confirmActionId) {
          try {
            await actOnCard(userId, card.id, confirmActionId, action.token);
          } catch {
            /* 409 / 已收起：重拉就是最新 */
          }
        }
        await refresh();
        return;
      }
      if (action.navigate?.back && confirmActionId) {
        rememberReturn({ cardId: card.id, actionId: confirmActionId, token: action.token, scopes });
      }
      closePanel();
      setReauthorizeError(
        new ShopifyScopeError(
          "Shopify hasn't given us a permission these tasks need. Approve it on Shopify and they'll continue.",
          check.missingScopes,
          check.authorizeUrl
        )
      );
    } finally {
      setBusyCardId(null);
    }
  };

  /**
   * 编辑器的出口回报给后端。
   *
   * <p>两个出口是后端给的两个 action id（`…:done` / `…:discarded`），弹窗在浏览器里关掉，
   * 没有别的东西能告诉服务端商家走的是哪一边。409 在这里不值得特殊处理 —— 卡已经被别的动作
   * 动过了，重拉一次就是最新的。
   */
  const reportEditorExit = async (
    card: RecoveryCard,
    action: CardAction,
    which: 'confirm' | 'discard'
  ) => {
    const actionId =
      which === 'confirm' ? action.modal?.confirmActionId : action.modal?.discardActionId;
    setEditing(null);
    if (userId && actionId) {
      try {
        await actOnCard(userId, card.id, actionId, action.token);
      } catch {
        /* 卡的去向不取决于这一次回报：任务成功后卡会自己收起（后端 recountAfterSuccess）。
           这里失败只是让它晚一点消失，不该把错误推给刚改完稿的商家。 */
      }
    }
    await refresh();
  };

  const handleAction = async (card: RecoveryCard, action: CardAction) => {
    if (!userId || busyCardId) return;

    if (action.kind === 'navigate' && action.navigate?.route === SHOPIFY_REAUTHORIZE_ROUTE) {
      await startShopifyReauthorize(card, action);
      return;
    }

    if (action.kind === 'navigate') {
      // 离开面板的仅有两类：站外授权与充值（后端注释原话）。路径归前端映射。
      //
      // [V0.9] 「完成后自动回任务中心并重开面板，该卡 ✓ 收起」。授权是在站外完成的，没有任何
      // 回调打给我们 —— 浏览器回来这件事本身就是唯一的信号，后端因此把要 POST 的 id 放在
      // navigate 里，明说它由「回来」触发而不是由某个按钮触发。
      //
      // 存进 sessionStorage 而不是 store：站外授权是一次整页跳转，回来时应用是重新启动的，
      // 内存里的东西一个都不剩。
      if (action.navigate?.back && action.navigate.confirmActionId) {
        rememberReturn({
          cardId: card.id,
          actionId: action.navigate.confirmActionId,
          token: action.token,
        });
      }
      const path = NAVIGATE_PATHS[action.navigate?.route ?? ''] ?? '/platform/settings';
      closePanel();
      navigate(path);
      return;
    }

    if (action.kind === 'modal') {
      // [V0.9⑤] 原地弹 Marketing Hub 的内容编辑弹窗。面板不自己做输入框 —— 改时间 / 改文案 /
      // 换图 / Publish Now / Discard 全在那个编辑器里，卡只负责把商家送到正确的那一篇。
      const draftId = action.modal?.params?.draftId ?? action.modal?.params?.taskId ?? '';
      if (!draftId) {
        // 卡上没有可开的篇目。跳去 Hub 总比把商家关在一个打不开的弹窗里强。
        closePanel();
        navigate('/ai-team/marketing/hub');
        return;
      }
      setEditing({ card, action, draftId });
      return;
    }

    // call：带 token 发；409 = 卡动了，用返回的当前卡重画并停手。
    setBusyCardId(card.id);
    try {
      await actOnCard(userId, card.id, action.id, action.token);
      // 一次修复可能连带收起别的卡（同因跨卡），所以不做原地增量，直接重拉。
      await refresh();
    } catch (e) {
      if (e instanceof CardActionConflict) {
        if (e.card) {
          setCards(prev => prev.map(c => (c.id === e.card!.id ? e.card! : c)));
        }
        await refresh();
      }
      // 其余错误：卡留在原地，商家可以再按 —— 面板没有比「按钮还在」更好的重试话术。
    } finally {
      setBusyCardId(null);
    }
  };

  const count = cards.length;

  return createPortal(
    <>
      {/* 只在有内容时挂：弹窗关着也常驻的话，每次面板渲染都要多背一个 HeroUI Modal。 */}
      {reauthorizeError ? (
        <Suspense fallback={null}>
          <ShopifyReconnectDialog
            error={reauthorizeError}
            isOpen
            onClose={() => setReauthorizeError(null)}
            resumesAutomatically
          />
        </Suspense>
      ) : null}
      <AnimatePresence>
        {open ? (
          <div className="fixed inset-0 z-[9999]">
            <motion.div
              className="absolute inset-0 bg-black/25"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePanel}
            />
            {/* 面板贴在左下店长头像浮标的右侧：浮标在左、面板在右、底对齐（Figma 24859:18400）。 */}
            <div className="absolute bottom-6 left-6 flex max-h-[calc(100vh-3rem)] items-end gap-3 lg:left-[248px]">
              <button
                type="button"
                onClick={closePanel}
                aria-label="Store Manager"
                className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-[#eaeff2] transition hover:shadow-xl"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f0ebff] text-[#6b33ff]">
                  <BuildingStorefrontIcon className="h-5 w-5" />
                </span>
                {count > 0 ? (
                  <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#eb001b] px-1 text-[11px] font-bold text-white ring-2 ring-white">
                    {count}
                  </span>
                ) : null}
              </button>
              <motion.div
                className="flex max-h-[calc(100vh-3rem)] w-[560px] max-w-[calc(100vw-6rem)] min-h-0 origin-bottom-left flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 12 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Header */}
                <div className="relative flex items-center gap-3 px-5 pb-3 pt-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f0ebff]">
                    <BuildingStorefrontIcon className="h-6 w-6 text-[#6b33ff]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-bold leading-tight text-[#101828]">
                      Store Manager
                    </h2>
                    <p className="mt-0.5 text-xs text-[#747c80]">
                      Everything that's stuck, across all agents
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closePanel}
                    aria-label="Close"
                    className="absolute right-3 top-4 rounded-lg p-1.5 text-[#98a2b3] transition hover:bg-[#f7f8fa] hover:text-[#475467]"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 pb-5">
                  {/* 「Reran N tasks after …」—— 系统自己修好的部分，面板的 summary 行（BE-2 的事件表数出来的） */}
                  {summary.length > 0 ? (
                    <div className="mb-4 space-y-2">
                      {summary.map(line => (
                        <div
                          key={line.cause}
                          className="flex items-start gap-3 rounded-xl border border-[#00bc2f]/10 bg-[#e5f8ea] p-4"
                        >
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00bc2f]">
                            <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} />
                          </span>
                          <p className="text-sm font-medium leading-5 text-[#747c80]">
                            {line.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {loading && count === 0 ? (
                    <div className="rounded-xl border border-[#eaeff2] bg-white px-4 py-10 text-center text-sm text-[#747c80]">
                      Loading…
                    </div>
                  ) : count === 0 ? (
                    <div className="rounded-xl border border-[#eaeff2] bg-white px-4 py-10 text-center text-sm text-[#747c80]">
                      You're all caught up.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {cards.map(card => (
                        <RecoveryCardView
                          key={card.id}
                          card={card}
                          focused={isFocusedCard(card, focusCardId, focusTaskId)}
                          busy={busyCardId === card.id}
                          onAction={handleAction}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
            {/* Open post：编辑器盖在面板之上，面板不关 —— 商家改完一篇，回到的还是那一列卡。 */}
            {editing && userId ? (
              <PanelPostEditor
                userId={userId}
                draftId={editing.draftId}
                onDone={() => void reportEditorExit(editing.card, editing.action, 'confirm')}
                onDiscarded={() => void reportEditorExit(editing.card, editing.action, 'discard')}
                onCancel={() => setEditing(null)}
              />
            ) : null}
          </div>
        ) : null}
      </AnimatePresence>
    </>,
    document.body
  );
}

export default StoreManagerPanel;
